import { Component, type ErrorInfo, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { EXTERNAL_REGISTER_SETTINGS_ROUTES } from "../externalRegisterRoutes";

type ExternalRegisterImportErrorBoundaryProps = {
  children: ReactNode;
  onReturnToLanding: () => void;
  strings: {
    title: string;
    description: string;
    alert: string;
    retry: string;
    back: string;
  };
};

type ExternalRegisterImportErrorBoundaryState = {
  error: Error | null;
};

class ExternalRegisterImportErrorBoundaryInner extends Component<
  ExternalRegisterImportErrorBoundaryProps,
  ExternalRegisterImportErrorBoundaryState
> {
  state: ExternalRegisterImportErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): ExternalRegisterImportErrorBoundaryState {
    return { error };
  }

  componentDidCatch(_error: Error, errorInfo: ErrorInfo): void {
    if (import.meta.env.DEV || import.meta.env.MODE === "test") {
      console.warn("[ExternalRegisterImport] Import view crashed.", {
        componentStack: errorInfo.componentStack,
      });
    }
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <Box sx={{ height: "100%", width: "100%", overflow: "auto", p: 3 }}>
        <Card variant="outlined" sx={{ maxWidth: 840, borderRadius: 1 }}>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {this.props.strings.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {this.props.strings.description}
                </Typography>
              </Box>

              <Alert severity="error" variant="outlined">
                {this.props.strings.alert}
              </Alert>

              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={this.reset}>
                  {this.props.strings.retry}
                </Button>
                <Button variant="outlined" onClick={this.props.onReturnToLanding}>
                  {this.props.strings.back}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }
}

export const ExternalRegisterImportErrorBoundary = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <ExternalRegisterImportErrorBoundaryInner
      onReturnToLanding={() => navigate(EXTERNAL_REGISTER_SETTINGS_ROUTES.root)}
      strings={{
        title: t("externalRegister.import.errorBoundary.title"),
        description: t("externalRegister.import.errorBoundary.description"),
        alert: t("externalRegister.import.errorBoundary.alert"),
        retry: t("externalRegister.import.errorBoundary.buttons.retry"),
        back: t("externalRegister.import.errorBoundary.buttons.back"),
      }}
    >
      {children}
    </ExternalRegisterImportErrorBoundaryInner>
  );
};
